/**
 * Shared reader for the Search Service's `/selection` endpoints.
 *
 * All four of them - live contacts, live companies, saved contacts, saved
 * companies - answer the same envelope:
 *
 *   { total, from, size, ids: string[] }
 *
 * The saved proxies wrap that again in this app's `{ success, data }`
 * envelope, so both nestings are accepted here rather than at each call site.
 *
 * A bare `string[]` is also tolerated: that is the shape the older fnx
 * selection endpoints returned (`v1/contactsSearchSelection` and
 * `v1/searchSelection/company` answered a plain array), and reading only the
 * array would silently yield `[]` against the current service - a bulk
 * "Select N" that appears to work but selects nothing.
 */
export function normalizeSelectionIds(response: unknown): string[] {
  if (Array.isArray(response)) {
    return response.map(String).filter(Boolean);
  }

  const envelope = response as { ids?: unknown; data?: { ids?: unknown } } | null | undefined;
  const ids = envelope?.ids ?? envelope?.data?.ids;

  if (!Array.isArray(ids)) return [];
  return ids.map(String).filter(Boolean);
}

/** Total number of rows matching the filters, as reported alongside the ids. */
export function readSelectionTotal(response: unknown): number {
  const envelope = response as { total?: unknown; data?: { total?: unknown } } | null | undefined;
  const total = envelope?.total ?? envelope?.data?.total;
  return typeof total === 'number' ? total : 0;
}
